import React from "react";

const TylerTriggs = () => {
  return (
    <div className="scientia-container">
      <div className="tyler-triggs-page">
        <div className="hero-section tyler-triggs-hero">
          <div className="hero-content">
            <div className="profile-section">
              <div className="profile-image">
                <img
                  alt="Tyler Triggs"
                  className="headshot"
                  src="/static/img/TylerTriggs-headshot.png"
                />
              </div>
              <div className="profile-info">
                <h1 className="profile-name">Tyler Triggs</h1>
                <p className="profile-title">
                  Senior Software Engineer & Creator of Scientia
                </p>
                <div className="profile-links">
                  <a
                    className="profile-link linkedin"
                    href="https://www.linkedin.com/in/tylertriggs/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <img
                      alt="LinkedIn"
                      className="profile-link-icon"
                      src="/static/img/linkedInBug-White.png"
                    />
                    LinkedIn
                  </a>
                  <a
                    className="profile-link github"
                    href="https://github.com/ttriggs"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <img
                      alt="GitHub"
                      className="profile-link-icon"
                      src="/static/img/github-mark-white.png"
                    />
                    GitHub
                  </a>
                  <a
                    className="profile-link blog"
                    href="http://tylertriggs.wordpress.com"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <img
                      alt="WordPress"
                      className="profile-link-icon"
                      src="/static/img/WordPress-logo.png"
                    />
                    Blog
                  </a>
                  <a
                    className="profile-link bandcamp"
                    href="http://vivifluent.bandcamp.com/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <img
                      alt="Bandcamp"
                      className="profile-link-icon"
                      src="/static/img/bandcamp-logotype-light-64.png"
                    />
                    Music
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="content-section">
          <div className="container">
            <div className="about-section">
              <h2>About Tyler</h2>
              <p className="about-text">
                Tyler is a product-minded software engineer with a unique journey from
                neuroscience research to full-stack development. After earning his BS in
                Neuroscience, he worked as a research technician at Massachusetts General
                Hospital, where data analysis and statistical processing sparked his
                interest in software development. This led him to complete a Ruby on Rails
                bootcamp in Boston before transitioning fully into tech.
              </p>
              <p className="about-text">
                Tyler spent several years as a Senior Engineer at{" "}
                <a
                  className="inline-link"
                  href="https://reverb.com/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Reverb
                </a>
                , the world&apos;s largest online marketplace for musical
                instruments—a perfect fit given his background as a musician.
                There he led high-visibility projects spanning payments,
                compliance, and innovative features like{" "}
                <a
                  className="inline-link"
                  href="https://reverb.com/featured/my-collection"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Gear Collections
                </a>{" "}
                and the pilot Trade-In for Cash program (now sunset). His passion
                for creating meaningful technology that connects people with
                their goals drives his work both professionally and in personal
                projects.
              </p>
              <p className="about-text">
                Currently, Tyler is focused on building Scientia and exploring new
                opportunities where he can apply his product engineering skills to create
                compelling user experiences. When he&apos;s not coding, you can find him
                making music as{" "}
                <a
                  className="inline-link"
                  href="http://vivifluent.bandcamp.com/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Vivifluent
                </a>
                , combining his technical and creative sides.
              </p>
            </div>

            <div className="scientia-section">
              <h2>Scientia: Personal AI Assistant</h2>
              <p className="scientia-intro">
                Scientia represents Tyler&apos;s vision for a truly proactive AI assistant
                that&apos;s easily customizable—bringing what you want from the internet
                to you before you have to think, ask, or remember. It keeps you connected
                to where you live and the things you want to be plugged into: local arts,
                events, opportunities, and personal interests.
              </p>

              <div className="scientia-features">
                <div className="feature-group">
                  <h3>
                    <span aria-label="brain" role="img">🧠</span>
                    {" "}Adaptive Learning
                  </h3>
                  <p>
                    Through onboarding interviews and ongoing conversations, Scientia iterates on its understanding
                    of user preferences, interests, hobbies, and regular activities. The system stores and manages personal facts automatically,
                    while allowing users to manually add or edit facts as desired.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="calendar" role="img">📅</span>
                    {" "}Intelligent Daily Insights
                  </h3>
                  <p>
                    Combining weather data, calendar information, and web research, Scientia delivers personalized daily
                    messages that are actually relevant to each user&apos;s life and interests.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="magnifying glass" role="img">🔍</span>
                    {" "}Rich Conversations
                  </h3>
                  <p>
                    The assistant provides detailed, contextual responses backed by
                    real-time web research workflows. Users can ask questions, request
                    specific research topics, or adjust their daily message preferences
                    through natural conversation.
                  </p>
                </div>
              </div>

              <div className="tech-stack">
                <h3>Technical Architecture</h3>
                <div className="tech-grid">
                  <div className="tech-category">
                    <h4>Backend</h4>
                    <ul>
                      <li>Django REST Framework</li>
                      <li>PostgreSQL</li>
                      <li>Redis & RabbitMQ</li>
                      <li>Celery for async tasks</li>
                    </ul>
                  </div>
                  <div className="tech-category">
                    <h4>Frontend</h4>
                    <ul>
                      <li>React & TypeScript</li>
                      <li>WebSocket real-time chat</li>
                      <li>BEM CSS methodology</li>
                      <li>Responsive design</li>
                    </ul>
                  </div>
                  <div className="tech-category">
                    <h4>AI & ML</h4>
                    <ul>
                      <li>LangChain & LangGraph</li>
                      <li>Multi-step LLM workflows</li>
                      <li>Structured data validation</li>
                      <li>Context-aware prompting</li>
                    </ul>
                  </div>
                  <div className="tech-category">
                    <h4>Infrastructure</h4>
                    <ul>
                      <li>Docker containerization</li>
                      <li>Heroku deployment</li>
                      <li>Poetry dependency management</li>
                      <li>Automated testing</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="invite-note">
                <small>
                  <em>
                    Interested in trying Scientia? Feel free to{" "}
                    <a
                      className="inline-link"
                      href="https://www.linkedin.com/in/tylertriggs/"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      reach out
                    </a>{" "}
                    for an invite code.
                  </em>
                </small>
              </p>
            </div>

            <div className="contact-section">
              <h2>Let&apos;s Connect</h2>
              <p className="contact-text">
                Interested in discussing opportunities, collaborating on projects, or learning more about Scientia?
                I&apos;d love to hear from you.
              </p>
              <div className="contact-methods">
                <a
                  className="contact-button"
                  href="https://www.linkedin.com/in/tylertriggs/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Connect on LinkedIn
                </a>
                <a
                  className="contact-button contact-button--secondary"
                  href="https://github.com/ttriggs"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  View GitHub Profile
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TylerTriggs;
